import { z } from 'zod'
import { DateTimeSchema } from '@omnea-app/packages/backend/general/zodDateTime'
import { httpUrlSchema } from '@omnea-app/packages/universal/schema/httpUrlSchema'
import {
  SUPPLIER_ONLINE_RESOURCE_STATUS_VALUES,
  SUPPLIER_ONLINE_RESOURCE_TYPE_VALUES,
} from '@omnea-app/packages/universal/suppliers/supplierEnums'

export const SupplierOnlineResourceStatusSchema = z
  .enum(SUPPLIER_ONLINE_RESOURCE_STATUS_VALUES)
  .describe(
    [
      'public: available online publicly',
      'private: available online but password protected or behind other measure',
      'unavailable: confirmed they do not have this resource',
      'notFound: could not find the resource but may exist if contacting the vendor',
    ].join('\n')
  )

export type SupplierOnlineResourceStatus = z.infer<typeof SupplierOnlineResourceStatusSchema>

export const SupplierOnlineResourceTypeSchema = z
  .enum(SUPPLIER_ONLINE_RESOURCE_TYPE_VALUES)
  .describe(
    [
      'trustCenter: A dedicated portal where vendors publish their security posture, certifications, and compliance documentation in one place.',
      "soc2: Service Organization Control 2 - an audit report verifying a vendor's controls around security, availability, processing integrity, confidentiality, and privacy.",
      'iso27001: An international standard for information security management systems (ISMS), demonstrating systematic controls to manage information security risks.',
      'privacyPolicy: A legal document explaining how the vendor collects, uses, stores, and shares personal data.',
      'dpa: A contract required under GDPR that governs how a vendor processes personal data on your behalf as a data processor.',
      "penTestReport: An external security assessment identifying vulnerabilities in the vendor's systems and infrastructure.",
      'governmentBusinessRegistry: A legal registration document or website record like Companies House in the UK showing the company is registered to legally operate.',
      'subprocessorList: A list of third-party providers the vendor uses to deliver their service - required for GDPR compliance.',
      'csaStar: Cloud Security Alliance Security, Trust, Assurance and Risk - a cloud-specific security certification and registry.',
      'pciDSS: Payment Card Industry Data Security Standard - required for vendors that handle or process credit card payments.',
      'cyberEssentials: A UK government-backed certification scheme that protects against common cyber threats. Relevant for UK-based vendors and contracts.',
      "statusPage: A real-time page showing the operational status and historical uptime of the vendor's services.",
    ].join('\n')
  )

export type SupplierOnlineResourceType = z.infer<typeof SupplierOnlineResourceTypeSchema>

export const SupplierOnlineResourceSchema = z.object({
  id: z.uuid(),
  organisationSupplierId: z.uuid(),
  name: z.string(),
  description: z.string(),
  status: SupplierOnlineResourceStatusSchema,
  evidenceUrl: httpUrlSchema.nullable(),
  datePublished: DateTimeSchema.nullable(),
  createdAt: DateTimeSchema,
  updatedAt: DateTimeSchema,
  organisationId: z.uuid(),
  resourceType: SupplierOnlineResourceTypeSchema,
  userId: z.uuid().nullable(),
})

export type SupplierOnlineResource = z.infer<typeof SupplierOnlineResourceSchema>

export const UpsertSupplierOnlineResourceInputSchema = SupplierOnlineResourceSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  organisationId: true,
})

export type UpsertSupplierOnlineResourceInput = z.infer<typeof UpsertSupplierOnlineResourceInputSchema>
